import { useHistory } from "react-router-dom";
import { Flex, Table, TableBody, TableCell, TableHeader, TableRow, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { DdqProdOpsTaskKind, DdqProdOpsTaskState } from "~/graphql/supergraphEnums";
import { SPACING } from "~/styles/variables";
import { TASK_KIND_LABELS, TASK_STATE_LABELS, WIZARD_STEP_LABELS } from "../labels";
import * as styles from "./styles_vanilla.css";
import type { DdqQueueTask } from "../types";

export type TasksTabProps = {
  tasks: DdqQueueTask[];
  targetId: string;
  isLocked: boolean;
};

// currentStep only adds signal mid-flight — noise once done, redundant before start.
const getStatusLabel = (task: DdqQueueTask): string => {
  if (task.blocked) return "Blocked";
  const stateLabel = TASK_STATE_LABELS[task.state];
  const stepLabel = task.currentStep ? WIZARD_STEP_LABELS[task.currentStep] : undefined;
  return task.state === DdqProdOpsTaskState.InProgress && stepLabel
    ? `${stateLabel} — ${stepLabel}`
    : stateLabel;
};

export const TasksTab = ({ tasks, targetId, isLocked }: TasksTabProps) => {
  const history = useHistory();
  const ordered = [...tasks].sort((a, b) => a.position - b.position);

  // Process: Start only covers pending→started; once in progress, the row is the way back in.
  // Review: the wizard starts it itself on landing, so its row is the way in from pending too.
  // Held/cancelled/completed requests freeze the tab — the overflow menu is the only way to act on them.
  const isReenterable = (task: DdqQueueTask) => {
    if (isLocked || task.blocked) return false;
    if (task.kind === DdqProdOpsTaskKind.Process) {
      return task.state === DdqProdOpsTaskState.InProgress;
    }
    if (task.kind === DdqProdOpsTaskKind.Review) {
      return task.state !== DdqProdOpsTaskState.Done;
    }
    return false;
  };

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.MEDIUM}
      className={styles.card}
      data-testid="ddq-drawer-tasks-tab-panel"
    >
      <Text variant="h3">Tasks</Text>

      {ordered.length === 0 ? (
        <Text variant="regular-md" color={Colors.Neutral700}>
          This request has no tasks.
        </Text>
      ) : (
        <Table variant="muted" hasBottomBorder>
          <TableHeader>
            <TableRow>
              <TableCell header>Name</TableCell>
              <TableCell header>Assigned</TableCell>
              <TableCell header rightAligned>
                Status
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordered.map((task) => (
              <TableRow
                key={task.id}
                data-testid={`ddq-drawer-task-${task.kind}`}
                className={isReenterable(task) ? styles.clickableTaskRow : undefined}
                onClick={
                  isReenterable(task)
                    ? () => history.push(`${globalEnv.ddqIntakeRoute}/${targetId}/process`)
                    : undefined
                }
              >
                <TableCell>
                  <Flex alignItems="center" gap={SPACING.SMALL}>
                    <Flex
                      className={
                        task.blocked ? styles.positionBadgeBlocked : styles.positionBadgeActive
                      }
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Text
                        variant="strong-sm"
                        color={task.blocked ? Colors.Neutral600 : Colors.Neutral0}
                      >
                        {task.position}
                      </Text>
                    </Flex>
                    <Text
                      variant="regular-md"
                      color={task.blocked ? Colors.Neutral600 : Colors.Neutral900}
                    >
                      {TASK_KIND_LABELS[task.kind]}
                    </Text>
                  </Flex>
                </TableCell>
                <TableCell color={task.assignee ? Colors.Neutral900 : Colors.Neutral600}>
                  {task.assignee?.fullName ?? "Unassigned"}
                </TableCell>
                <TableCell
                  rightAligned
                  color={task.blocked ? Colors.Neutral600 : Colors.Neutral900}
                >
                  <Flex alignItems="center" justifyContent="flex-end" gap={SPACING.SMALL}>
                    {getStatusLabel(task)}
                    {task.blocked && (
                      <Icon
                        type="lock"
                        variant="outline"
                        size="small"
                        color={Colors.Neutral600}
                        data-testid={`ddq-drawer-task-${task.kind}-lock`}
                      />
                    )}
                  </Flex>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Flex>
  );
};
